import type React from "react";
import { useTranslation } from "react-i18next";
import {
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LogoutIcon from "@mui/icons-material/Logout";
import { profileStore } from "@/stores/Stores";

const Sessions: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const userProfile = profileStore.profile;

  const sessions = userProfile?.sessions?.slice().sort((a, b) => {
    const dateA = new Date(Number(a.timestamp) * 1000);
    const dateB = new Date(Number(b.timestamp) * 1000);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <>
      <Typography variant="h4">{t("Active Sessions")}</Typography>

      <Table sx={{ marginTop: "1rem" }}>
        <TableHead>
          <TableRow>
            <TableCell align="left" component="th">
              <Typography variant="button">{t("Timestamp")}</Typography>
            </TableCell>
            <TableCell align="left" component="th">
              <Typography variant="button">{t("Source")}</Typography>
            </TableCell>
            <TableCell align="center" padding="none" component="th" sx={{ width: "4rem" }}>
              <Typography variant="button">{t("Action")}</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions && sessions.length > 0 ? (
            sessions.map((row) => {
              const isCurrentSession = profileStore.isCurrentSession(row.session_id);
              return (
                <TableRow
                  key={row.session_id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: isCurrentSession ? "rgba(25, 118, 210, 0.08)" : "inherit",
                  }}
                >
                  <TableCell
                    align="left"
                    sx={{
                      width: "14rem",
                      [theme.breakpoints.down("sm")]: {
                        width: "6rem",
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: "var(--font-size-m)" }}>
                      {new Date(Number(row.timestamp) * 1000).toLocaleString()}
                      {isCurrentSession && (
                        <Chip
                          label="Current"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ marginLeft: "1ex", fontSize: "var(--font-size-s)" }}
                        />
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography variant="body2" sx={{ fontSize: "var(--font-size-m)" }}>
                      {row.source}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" padding="none" sx={{ width: "4rem" }}>
                    <IconButton
                      edge="end"
                      sx={{ marginRight: "0.5rem" }}
                      onClick={() =>
                        isCurrentSession ? profileStore.logout() : profileStore.revokeSession(row.session_id)
                      }
                      title={isCurrentSession ? "Log out" : "Revoke this session"}
                    >
                      {isCurrentSession ? (
                        <LogoutIcon
                          sx={{
                            width: "24px",
                            height: "24px",
                          }}
                        />
                      ) : (
                        <DeleteIcon
                          sx={{
                            width: "24px",
                            height: "24px",
                            color: "inherit",
                          }}
                        />
                      )}
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow sx={{ height: "4.5rem" }}>
              <TableCell colSpan={3} align="center">
                <Typography variant="body2" sx={{ fontSize: "var(--font-size-m)" }}>
                  Loading...
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

export default Sessions;
